// The session's discharge (spec Q9): the existing IPD five-step pipeline and
// its role rules, driven from here with the exported dischargeProcessApi
// functions. Each stage shows its step; the server's stage rules decide who may
// take it, and a refusal is shown as the server words it. Billing clears from
// its own IPD Billing -> Discharge queue, as for any inpatient.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { toast } from "react-toastify";

import {
  STAGE_LABELS,
  advanceDischargeProcess,
  createDischargeProcess,
  fetchDischargeProcess,
  updateDischargeChecklist,
} from "../../ipd/api/dischargeProcessApi";
import DischargeSummaryModal from "../../ipd/components/modals/DischargeSummaryModal";
import { fetchHasDischargeAdvice, fileDischargeAdvice } from "../api/dischargeFlow";
import { IPD_COLORS, IpdButton, IpdSectionCard } from "../../ipd/utils";
import { dischargeSteps } from "../utils/sessionView";
import { formatIstTime, istDayKey } from "../../../utils/istDateTime";

const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}` });

/** The step list, drawn like IPD Billing's "Settlement Status". */
function StepList({ steps }) {
  return (
    <Box sx={{ mb: 2 }}>
      {steps.map((step, index) => {
        const done = step.status === "done";
        const active = step.status === "in-progress";
        const color = done ? IPD_COLORS.success.text : active ? IPD_COLORS.info.text : IPD_COLORS.border;
        const last = index === steps.length - 1;
        return (
          <Box key={step.label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", mt: "3px", border: `2px solid ${color}`,
                bgcolor: done || active ? color : IPD_COLORS.cardBg }} />
              {!last && <Box sx={{ width: 2, height: 22, mt: "2px", bgcolor: done ? IPD_COLORS.success.text : IPD_COLORS.border }} />}
            </Box>
            <Typography sx={{ fontSize: 13, pb: last ? 0 : 1.25, fontWeight: done || active ? 600 : 400,
              color: done || active ? IPD_COLORS.textPrimary : IPD_COLORS.textSecondary }}>
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function DischargePanel({ session, shiftEnd, onChanged }) {
  const [process, setProcess] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [adviceFiled, setAdviceFiled] = useState(false);

  // "Not started" is a Success with no data; a throw is a real failure, and
  // must not read as "Not started" and offer to initiate a second process.
  const load = useCallback(async () => {
    try {
      const res = await fetchDischargeProcess(session.admission_id);
      const row = res?.data || null;
      setProcess(row);
      setChecklist(row?.discharge_checklist || {});
      // Discharge starts only after the doctor's advice, as on the nurse dashboard.
      setAdviceFiled(row ? true : await fetchHasDischargeAdvice(session.admission_id));
      setLoadError("");
    } catch (err) {
      setLoadError(err.response?.data?.message || err.message || "Could not load the discharge");
    }
  }, [session.admission_id]);

  useEffect(() => { load(); }, [load]);

  const run = async (work, success) => {
    setBusy(true);
    try {
      // dischargeProcessApi returns the server envelope; fileDischargeAdvice
      // returns { ok, message }.
      const res = await work();
      const succeeded = res?.ok ?? res?.status === "Success";
      if (succeeded) toast.success(success);
      else toast.error(res?.message || "Refused");
    } catch (err) {
      toast.error(serverMessage(err, "Something went wrong"));
    } finally {
      setBusy(false);
      load();
      onChanged();
    }
  };

  const stage = process?.stage;
  return (
    <IpdSectionCard
      title="Discharge"
      subtitle={stage ? `Stage: ${STAGE_LABELS[stage] || stage}` : "Not started"}
    >
      {loadError && (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.error.text }}>{loadError}</Typography>
          <IpdButton size="small" variant="outlined" onClick={load}>Retry</IpdButton>
        </Box>
      )}
      {!loadError && <StepList steps={dischargeSteps(process, adviceFiled)} />}
      {!loadError && !process && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <IpdButton size="small" variant="outlined" disabled={busy || adviceFiled}
            onClick={() => run(() => fileDischargeAdvice(session.admission_id, {
              date: istDayKey(new Date()),
              time: shiftEnd || formatIstTime(new Date()),
              notes: "Day care session complete",
            }), "Discharge advice filed")}
          >
            File discharge advice (doctor)
          </IpdButton>
          <IpdButton size="small" disabled={busy || !adviceFiled}
            onClick={() => run(() => createDischargeProcess({ admissionId: session.admission_id, outcome: "alive" }),
              "Discharge initiated")}
          >
            Initiate discharge (nurse)
          </IpdButton>
          {!adviceFiled && (
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, width: "100%" }}>
              Awaiting the doctor's discharge advice.
            </Typography>
          )}
        </Box>
      )}

      {stage === "ward" && (
        <Box>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 1 }}>
            {`Summary: ${process.summary_status || "draft"}`}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <IpdButton size="small" variant="outlined" onClick={() => setSummaryOpen(true)}>
              Write / approve summary (doctor)
            </IpdButton>
            <IpdButton size="small" disabled={busy || process.summary_status !== "approved"}
              onClick={() => run(() => advanceDischargeProcess(process.id), "Sent to billing")}>
              Complete &amp; send to billing
            </IpdButton>
          </Box>
        </Box>
      )}

      {stage === "billing" && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          Waiting for billing: the session is cleared from IPD Billing → Discharge, where the No-Due certificate is issued.
        </Typography>
      )}

      {stage === "nursing_discharge" && (
        <Box>
          {(process.checklist_items || []).map((item) => (
            <FormControlLabel
              key={item.key}
              sx={{ display: "flex", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
              label={`${item.label}${item.required ? "" : " (optional)"}`}
              control={(
                <Checkbox checked={Boolean(checklist[item.key])}
                  onChange={(e) => setChecklist({ ...checklist, [item.key]: e.target.checked })} />
              )}
            />
          ))}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
            <IpdButton size="small" variant="outlined" disabled={busy}
              onClick={() => run(() => updateDischargeChecklist(process.id, checklist), "Checklist saved")}>
              Save checklist
            </IpdButton>
            <IpdButton size="small" disabled={busy || (process.checklist_incomplete || []).length > 0}
              onClick={() => run(() => advanceDischargeProcess(process.id), "Physically discharged")}>
              Mark physically discharged (nurse)
            </IpdButton>
          </Box>
        </Box>
      )}

      <DischargeSummaryModal
        open={summaryOpen}
        onClose={() => { setSummaryOpen(false); load(); }}
        admissionId={session.admission_id}
        patientName={session.patient_name}
        patientBedNo={session.bed_code}
        patientUhid={session.patient_id}
        authHeaders={authHeaders()}
        admittingDoctorId={session.ordered_by_doctor_id}
      />
    </IpdSectionCard>
  );
}
